'use client';

import { c as _c } from "react/compiler-runtime";
import { format } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import Captcha from 'react-google-recaptcha';
import { useWatch } from 'react-hook-form';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { Input } from '~/components/Input';
import { MarketingOptOutField, PRIVACY_NOTICE_ID, PrivacyNotice, privacyPolicyApplies } from '~/components/MarketingConsentFields';
import { Notification } from '~/components/Notification';
import { RadioButton, RadioButtonGroup } from '~/components/RadioButtonGroup';
import { Select } from '~/components/Select';
import { Textarea } from '~/components/TextArea';
import { COUNTRY_IDS } from '~/constants/countries';
import { useConciergeForm } from '~/hooks/useConciergeForm';
import { leadPassengerCountryOptions } from '~/schemas/contactFormSchema';
import { capitalizeWords, syncAutofilledInputs } from '~/utils/formHelper';
import { isSingleOptInEnabled } from '~/utils/singleOptInFlag';
import styles from './ConciergeForm.module.css';
const ConciergeForm = () => {
  const $ = _c(332);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("content.forms.reason_custom_trip");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      id: "I'd like a custom trip",
      label: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("content.forms.reason_accessibility");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      id: "I have accessibility requirements",
      label: t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("content.forms.reason_other");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = {
      id: "Any other reason",
      label: t4
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t5) {
    t6 = [t1, t3, t5];
    $[12] = t1;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const reasonOptions = t6;
  let t7;
  if ($[16] !== t) {
    t7 = t("forms.budget_1000_1500");
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = {
      id: "1000-1500",
      label: t7
    };
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("forms.budget_1500_2000");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = {
      id: "1500-2000",
      label: t9
    };
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t("forms.budget_2000_3000");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = {
      id: "2000-3000",
      label: t11
    };
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("forms.budget_3000_5000");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = {
      id: "3000-5000",
      label: t13
    };
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("forms.budget_5000_plus");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = {
      id: "5000+",
      label: t15
    };
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t14 || $[39] !== t16 || $[40] !== t8) {
    t17 = [t8, t10, t12, t14, t16];
    $[36] = t10;
    $[37] = t12;
    $[38] = t14;
    $[39] = t16;
    $[40] = t8;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const budgetOptions = t17;
  const {
    Controller,
    control,
    getValues,
    handleSubmit,
    formState: t18,
    watch,
    register,
    setValue,
    onSubmit,
    captchaRef,
    onCaptchaErrored,
    setFocus,
    autofill,
    submitStatus
  } = useConciergeForm();
  const {
    errors,
    isSubmitting
  } = t18;
  const formRef = useRef(null);
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = ["newsletterSignup", "marketingOptOut"];
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== control) {
    t20 = {
      control,
      name: t19
    };
    $[43] = control;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const [newsletterSignup, marketingOptOut] = useWatch(t20);
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = isSingleOptInEnabled();
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const singleOptIn = t21;
  let t22;
  if ($[46] !== marketingOptOut || $[47] !== newsletterSignup) {
    t22 = privacyPolicyApplies({
      newsletterSignup,
      marketingOptOut
    });
    $[46] = marketingOptOut;
    $[47] = newsletterSignup;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const privacyApplies = t22;
  const outcomeRef = useRef(null);
  let t23;
  if ($[49] !== submitStatus?.state) {
    t23 = () => {
      if (submitStatus?.state === "error") {
        outcomeRef.current?.focus();
      }
    };
    $[49] = submitStatus?.state;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== submitStatus) {
    t24 = [submitStatus];
    $[51] = submitStatus;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  useEffect(t23, t24);
  const heardAbout = watch("howDidYouHearAboutUs");
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = ["Social", "Press", "TravelAgent", "Other"];
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  const needsHeardAboutDetail = t25.includes(heardAbout ?? "");
  let t26;
  if ($[54] !== t) {
    t26 = t("content.forms.heard_social_label");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t) {
    t27 = t("content.forms.heard_social_placeholder");
    $[56] = t;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t26 || $[59] !== t27) {
    t28 = {
      id: "socialPlatform",
      label: t26,
      placeholder: t27
    };
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t) {
    t29 = t("content.forms.heard_press_label");
    $[61] = t;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t) {
    t30 = t("content.forms.heard_press_placeholder");
    $[63] = t;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t29 || $[66] !== t30) {
    t31 = {
      id: "pressSource",
      label: t29,
      placeholder: t30
    };
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t) {
    t32 = t("content.forms.heard_travel_agent_label");
    $[68] = t;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t) {
    t33 = t("content.forms.heard_travel_agent_placeholder");
    $[70] = t;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t32 || $[73] !== t33) {
    t34 = {
      id: "TravelAgent",
      label: t32,
      placeholder: t33
    };
    $[72] = t32;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t) {
    t35 = t("content.forms.heard_other_label");
    $[75] = t;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t) {
    t36 = t("content.forms.heard_other_placeholder");
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t35 || $[80] !== t36) {
    t37 = {
      id: "otherSource",
      label: t35,
      placeholder: t36
    };
    $[79] = t35;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t28 || $[83] !== t31 || $[84] !== t34 || $[85] !== t37) {
    t38 = {
      Social: t28,
      Press: t31,
      TravelAgent: t34,
      Other: t37
    };
    $[82] = t28;
    $[83] = t31;
    $[84] = t34;
    $[85] = t37;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  const heardAboutDetailMeta = t38;
  let t39;
  if ($[87] !== watch) {
    t39 = watch(["elderlyPassenger"]);
    $[87] = watch;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  const enableNumberOfElderlyPassengers = t39;
  const prefferedContactMethod = watch("preferredContactMethod") === "phone";
  const childCount = watch("childCount");
  let t40;
  if ($[89] !== setFocus) {
    t40 = errs => {
      const first = Object.keys(errs)[0] as keyof typeof errs | undefined;
      if (!first) {
        return;
      }
      setFocus(first as any, {
        shouldSelect: false
      });
      requestAnimationFrame(() => {
        document.querySelector(`[name="${String(first)}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      });
    };
    $[89] = setFocus;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  const onInvalid = t40;
  let t41;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = format(new Date(), "yyyy-MM-dd");
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  const minDate = t41;
  const notProduction = process.env.NEXT_PUBLIC_VERCEL_ENV !== "production";
  let t42;
  if ($[92] !== getValues || $[93] !== handleSubmit || $[94] !== onInvalid || $[95] !== onSubmit || $[96] !== setValue) {
    t42 = event => {
      syncAutofilledInputs(formRef.current, getValues, setValue);
      return handleSubmit(onSubmit, onInvalid)(event);
    };
    $[92] = getValues;
    $[93] = handleSubmit;
    $[94] = onInvalid;
    $[95] = onSubmit;
    $[96] = setValue;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== captchaRef || $[99] !== onCaptchaErrored) {
    t43 = <Captcha ref={captchaRef} size="invisible" sitekey={process.env.NEXT_PUBLIC_RECAPTCHA ?? ""} onErrored={onCaptchaErrored} />;
    $[98] = captchaRef;
    $[99] = onCaptchaErrored;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== autofill) {
    t44 = notProduction ? <Button onClick={() => autofill("solo")}>Autofill</Button> : null;
    $[101] = autofill;
    $[102] = t44;
  } else {
    t44 = $[102];
  }
  let t45;
  if ($[103] !== register) {
    t45 = register("middleName");
    $[103] = register;
    $[104] = t45;
  } else {
    t45 = $[104];
  }
  let t46;
  if ($[105] !== t45) {
    t46 = <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" placeholder="Leave this field empty" className={styles.honeypot} {...t45} />;
    $[105] = t45;
    $[106] = t46;
  } else {
    t46 = $[106];
  }
  let t47;
  if ($[107] !== errors.whyGetIntouch || $[108] !== reasonOptions || $[109] !== t) {
    t47 = t48 => {
      const {
        field
      } = t48;
      return <Select {...field} id="whyGetIntouch" name={field.name} label={t("content.forms.reason_label")} selected={field.value} onChange={e => field.onChange(e.target.value)} onBlur={field.onBlur} errorMessage={typeof errors.whyGetIntouch?.message === "string" ? errors.whyGetIntouch.message : ""} required={true}><option value="" disabled={true}>{t("content.forms.reason_placeholder")}</option>{reasonOptions.map(_temp)}</Select>;
    };
    $[107] = errors.whyGetIntouch;
    $[108] = reasonOptions;
    $[109] = t;
    $[110] = t47;
  } else {
    t47 = $[110];
  }
  let t48;
  if ($[111] !== Controller || $[112] !== control || $[113] !== t47) {
    t48 = <Controller name="whyGetIntouch" control={control} render={t47} />;
    $[111] = Controller;
    $[112] = control;
    $[113] = t47;
    $[114] = t48;
  } else {
    t48 = $[114];
  }
  let t49;
  if ($[115] !== errors.whereToGo || $[116] !== t) {
    t49 = t50 => {
      const {
        field: field_0
      } = t50;
      return <Select {...field_0} id="whereToGo" label={t("content.forms.destination_label")} onChange={e_0 => field_0.onChange(e_0.target.value)} onBlur={field_0.onBlur} errorMessage={typeof errors.whereToGo?.message === "string" ? errors.whereToGo.message : ""} required={true} helpText={<span>{t("content.forms.destination_help")}</span>}><option value="" disabled={true}>{t("forms.destination_placeholder")}</option>{[...Object.keys(COUNTRY_IDS), "Tunisia"].sort(_temp2).map(_temp3)}<option value="Other">{t("common.other")}</option></Select>;
    };
    $[115] = errors.whereToGo;
    $[116] = t;
    $[117] = t49;
  } else {
    t49 = $[117];
  }
  let t50;
  if ($[118] !== Controller || $[119] !== control || $[120] !== t49) {
    t50 = <Controller name="whereToGo" control={control} render={t49} />;
    $[118] = Controller;
    $[119] = control;
    $[120] = t49;
    $[121] = t50;
  } else {
    t50 = $[121];
  }
  let t51;
  if ($[122] !== t) {
    t51 = t52 => {
      const {
        field: t53
      } = t52;
      const {
        value,
        onChange
      } = t53;
      return <Input id="bestStation" name="bestStation" label={t("content.forms.station_label")} value={value} onChange={onChange} helpText={t("content.forms.station_help")} />;
    };
    $[122] = t;
    $[123] = t51;
  } else {
    t51 = $[123];
  }
  let t52;
  if ($[124] !== Controller || $[125] !== control || $[126] !== t51) {
    t52 = <Controller name="bestStation" control={control} render={t51} />;
    $[124] = Controller;
    $[125] = control;
    $[126] = t51;
    $[127] = t52;
  } else {
    t52 = $[127];
  }
  let t53;
  if ($[128] !== t) {
    t53 = t54 => {
      const {
        field: t55
      } = t54;
      const {
        value: value_0,
        onChange: onChange_0
      } = t55;
      return <Input id="extraPlaces" name="extraPlaces" value={value_0} onChange={onChange_0} label={t("content.forms.places_label")} placeholder={t("content.forms.places_placeholder")} />;
    };
    $[128] = t;
    $[129] = t53;
  } else {
    t53 = $[129];
  }
  let t54;
  if ($[130] !== Controller || $[131] !== control || $[132] !== t53) {
    t54 = <Controller name="extraPlaces" control={control} render={t53} />;
    $[130] = Controller;
    $[131] = control;
    $[132] = t53;
    $[133] = t54;
  } else {
    t54 = $[133];
  }
  let t55;
  if ($[134] !== errors.whenTravelling || $[135] !== t) {
    t55 = t56 => {
      const {
        field: field_1
      } = t56;
      return <Input {...field_1} aria-required={true} type="date" id="whenTravelling" name="whenTravelling" label={t("content.forms.date_label")} errorMessage={typeof errors.whenTravelling?.message === "string" ? errors.whenTravelling.message : undefined} required={true} min={minDate} />;
    };
    $[134] = errors.whenTravelling;
    $[135] = t;
    $[136] = t55;
  } else {
    t55 = $[136];
  }
  let t56;
  if ($[137] !== Controller || $[138] !== control || $[139] !== t55) {
    t56 = <Controller name="whenTravelling" control={control} render={t55} />;
    $[137] = Controller;
    $[138] = control;
    $[139] = t55;
    $[140] = t56;
  } else {
    t56 = $[140];
  }
  let t57;
  if ($[141] !== t) {
    t57 = t58 => {
      const {
        field: field_2
      } = t58;
      return <section><Input id="flexibility" {...field_2} label={t("content.forms.flexibility_label")} helpText={t("content.forms.flexibility_help")} placeholder={t("content.forms.flexibility_placeholder")} /></section>;
    };
    $[141] = t;
    $[142] = t57;
  } else {
    t57 = $[142];
  }
  let t58;
  if ($[143] !== Controller || $[144] !== control || $[145] !== t57) {
    t58 = <Controller name="flexibility" control={control} render={t57} />;
    $[143] = Controller;
    $[144] = control;
    $[145] = t57;
    $[146] = t58;
  } else {
    t58 = $[146];
  }
  let t59;
  if ($[147] !== errors.travelDuration || $[148] !== t) {
    t59 = t60 => {
      const {
        field: field_3
      } = t60;
      return <Input id="travelDuration" {...field_3} label={t("content.forms.duration_label")} placeholder={t("content.forms.duration_placeholder")} errorMessage={typeof errors.travelDuration?.message === "string" ? errors.travelDuration.message : undefined} required={true} />;
    };
    $[147] = errors.travelDuration;
    $[148] = t;
    $[149] = t59;
  } else {
    t59 = $[149];
  }
  let t60;
  if ($[150] !== Controller || $[151] !== control || $[152] !== t59) {
    t60 = <Controller name="travelDuration" control={control} render={t59} />;
    $[150] = Controller;
    $[151] = control;
    $[152] = t59;
    $[153] = t60;
  } else {
    t60 = $[153];
  }
  let t61;
  if ($[154] !== budgetOptions || $[155] !== errors.budget || $[156] !== t) {
    t61 = t62 => {
      const {
        field: field_4
      } = t62;
      return <Select id="budget" {...field_4} label={t("content.forms.budget_label")} selected={field_4.value} onChange={e_1 => field_4.onChange(e_1.target.value)} onBlur={field_4.onBlur} errorMessage={typeof errors.budget?.message === "string" ? errors.budget.message : undefined} required={true}><option value="" disabled={true}>{t("forms.budget_placeholder")}</option>{budgetOptions.map(_temp4)}</Select>;
    };
    $[154] = budgetOptions;
    $[155] = errors.budget;
    $[156] = t;
    $[157] = t61;
  } else {
    t61 = $[157];
  }
  let t62;
  if ($[158] !== Controller || $[159] !== control || $[160] !== t61) {
    t62 = <Controller name="budget" control={control} render={t61} />;
    $[158] = Controller;
    $[159] = control;
    $[160] = t61;
    $[161] = t62;
  } else {
    t62 = $[161];
  }
  let t63;
  if ($[162] !== t) {
    t63 = t("content.forms.passengers_label");
    $[162] = t;
    $[163] = t63;
  } else {
    t63 = $[163];
  }
  let t64;
  if ($[164] !== t63) {
    t64 = <p>{t63}</p>;
    $[164] = t63;
    $[165] = t64;
  } else {
    t64 = $[165];
  }
  let t65;
  if ($[166] !== errors.adultCount || $[167] !== t) {
    t65 = t66 => {
      const {
        field: field_5
      } = t66;
      return <section className="w-1/2"><Input id="adultCount" {...field_5} onBeforeInput={_temp5} onPaste={_temp6} value={Number(field_5.value)} label={t("content.forms.adults_label")} pattern="[0-9]*" errorMessage={typeof errors.adultCount?.message === "string" ? errors.adultCount.message : undefined} required={true} /></section>;
    };
    $[166] = errors.adultCount;
    $[167] = t;
    $[168] = t65;
  } else {
    t65 = $[168];
  }
  let t66;
  if ($[169] !== Controller || $[170] !== control || $[171] !== t65) {
    t66 = <Controller name="adultCount" control={control} render={t65} />;
    $[169] = Controller;
    $[170] = control;
    $[171] = t65;
    $[172] = t66;
  } else {
    t66 = $[172];
  }
  let t67;
  if ($[173] !== t) {
    t67 = t68 => {
      const {
        field: t69
      } = t68;
      const {
        value: value_1,
        onChange: onChange_1
      } = t69;
      return <section className="w-1/2"><Input id="childCount" name="childCount" value={Number(value_1)} onChange={onChange_1} onBeforeInput={_temp7} onPaste={_temp8} label={t("content.forms.children_label")} pattern="[0-9]*" /></section>;
    };
    $[173] = t;
    $[174] = t67;
  } else {
    t67 = $[174];
  }
  let t68;
  if ($[175] !== Controller || $[176] !== control || $[177] !== t67) {
    t68 = <Controller name="childCount" control={control} render={t67} />;
    $[175] = Controller;
    $[176] = control;
    $[177] = t67;
    $[178] = t68;
  } else {
    t68 = $[178];
  }
  let t69;
  if ($[179] !== t66 || $[180] !== t68) {
    t69 = <section className={styles.inlineFields}>{t66}{t68}</section>;
    $[179] = t66;
    $[180] = t68;
    $[181] = t69;
  } else {
    t69 = $[181];
  }
  let t70;
  if ($[182] !== Controller || $[183] !== childCount || $[184] !== control || $[185] !== errors.childAges || $[186] !== t) {
    t70 = childCount > 0 ? <Controller name="childAges" control={control} render={t71 => {
      const {
        field: field_6
      } = t71;
      return <Input id="childAges" {...field_6} label={t("content.forms.child_ages_label")} errorMessage={typeof errors.childAges?.message === "string" ? errors.childAges.message : undefined} required={true} />;
    }} /> : null;
    $[182] = Controller;
    $[183] = childCount;
    $[184] = control;
    $[185] = errors.childAges;
    $[186] = t;
    $[187] = t70;
  } else {
    t70 = $[187];
  }
  let t71;
  if ($[188] !== errors.rooms || $[189] !== t) {
    t71 = t72 => {
      const {
        field: t73
      } = t72;
      const {
        value: value_2,
        onChange: onChange_2
      } = t73;
      return <Input required={true} id="rooms" name="rooms" value={Number(value_2)} onChange={onChange_2} onBeforeInput={_temp9} onPaste={_temp10} label={t("forms.rooms_label")} pattern="[0-9]*" errorMessage={typeof errors.rooms?.message === "string" ? errors.rooms.message : undefined} />;
    };
    $[188] = errors.rooms;
    $[189] = t;
    $[190] = t71;
  } else {
    t71 = $[190];
  }
  let t72;
  if ($[191] !== Controller || $[192] !== control || $[193] !== t71) {
    t72 = <Controller name="rooms" control={control} render={t71} />;
    $[191] = Controller;
    $[192] = control;
    $[193] = t71;
    $[194] = t72;
  } else {
    t72 = $[194];
  }
  let t73;
  if ($[195] !== errors.elderlyPassenger || $[196] !== t) {
    t73 = t74 => {
      const {
        field: field_7
      } = t74;
      return <Select id="elderlyPassenger" name={field_7.name} label={t("content.forms.elderly_label")} selected={field_7.value} onChange={e_8 => field_7.onChange(e_8.target.value)} onBlur={field_7.onBlur} errorMessage={typeof errors.elderlyPassenger?.message === "string" ? errors.elderlyPassenger.message : undefined} required={true}><option value="" disabled={true}>{t("forms.select_placeholder")}</option><option value="Yes">{t("common.yes")}</option><option value="No">{t("common.no")}</option></Select>;
    };
    $[195] = errors.elderlyPassenger;
    $[196] = t;
    $[197] = t73;
  } else {
    t73 = $[197];
  }
  let t74;
  if ($[198] !== Controller || $[199] !== control || $[200] !== t73) {
    t74 = <Controller name="elderlyPassenger" control={control} render={t73} />;
    $[198] = Controller;
    $[199] = control;
    $[200] = t73;
    $[201] = t74;
  } else {
    t74 = $[201];
  }
  let t75;
  if ($[202] !== Controller || $[203] !== control || $[204] !== enableNumberOfElderlyPassengers[0] || $[205] !== t) {
    t75 = enableNumberOfElderlyPassengers[0] === "Yes" ? <Controller name="numberOfElderlyPassengers" control={control} render={t76 => {
      const {
        field: t77
      } = t76;
      const {
        value: value_3,
        onChange: onChange_3
      } = t77;
      return <Input id="numberOfElderlyPassengers" name="numberOfElderlyPassengers" value={value_3} onChange={onChange_3} label={t("content.forms.elderly_count_label")} pattern="[0-9]*" onBeforeInput={_temp11} onPaste={_temp12} />;
    }} /> : null;
    $[202] = Controller;
    $[203] = control;
    $[204] = enableNumberOfElderlyPassengers[0];
    $[205] = t;
    $[206] = t75;
  } else {
    t75 = $[206];
  }
  let t76;
  if ($[207] !== t) {
    t76 = t77 => {
      const {
        field: t78
      } = t77;
      const {
        value: value_4,
        onChange: onChange_4
      } = t78;
      return <Textarea id="anythingElse" name="anythingElse" value={value_4} onChange={onChange_4} label={t("content.forms.anything_else_label")} rows={4} />;
    };
    $[207] = t;
    $[208] = t76;
  } else {
    t76 = $[208];
  }
  let t77;
  if ($[209] !== Controller || $[210] !== control || $[211] !== t76) {
    t77 = <Controller name="anythingElse" control={control} render={t76} />;
    $[209] = Controller;
    $[210] = control;
    $[211] = t76;
    $[212] = t77;
  } else {
    t77 = $[212];
  }
  let t78;
  if ($[213] !== t) {
    t78 = t("content.forms.contact_heading");
    $[213] = t;
    $[214] = t78;
  } else {
    t78 = $[214];
  }
  let t79;
  if ($[215] !== t78) {
    t79 = <p>{t78}</p>;
    $[215] = t78;
    $[216] = t79;
  } else {
    t79 = $[216];
  }
  let t80;
  if ($[217] !== errors.firstName || $[218] !== t) {
    t80 = t81 => {
      const {
        field: field_8
      } = t81;
      return <section className="w-1/2"><Input id="firstName" {...field_8} label={t("forms.name_first_label")} errorMessage={typeof errors.firstName?.message === "string" ? errors.firstName.message : undefined} required={true} /></section>;
    };
    $[217] = errors.firstName;
    $[218] = t;
    $[219] = t80;
  } else {
    t80 = $[219];
  }
  let t81;
  if ($[220] !== Controller || $[221] !== control || $[222] !== t80) {
    t81 = <Controller name="firstName" control={control} render={t80} />;
    $[220] = Controller;
    $[221] = control;
    $[222] = t80;
    $[223] = t81;
  } else {
    t81 = $[223];
  }
  let t82;
  if ($[224] !== errors.lastName || $[225] !== t) {
    t82 = t83 => {
      const {
        field: field_9
      } = t83;
      return <section className="w-1/2"><Input id="lastName" {...field_9} label={t("forms.name_last_label")} errorMessage={typeof errors.lastName?.message === "string" ? errors.lastName.message : undefined} required={true} /></section>;
    };
    $[224] = errors.lastName;
    $[225] = t;
    $[226] = t82;
  } else {
    t82 = $[226];
  }
  let t83;
  if ($[227] !== Controller || $[228] !== control || $[229] !== t82) {
    t83 = <Controller name="lastName" control={control} render={t82} />;
    $[227] = Controller;
    $[228] = control;
    $[229] = t82;
    $[230] = t83;
  } else {
    t83 = $[230];
  }
  let t84;
  if ($[231] !== t81 || $[232] !== t83) {
    t84 = <section className={styles.inlineFields}>{t81}{t83}</section>;
    $[231] = t81;
    $[232] = t83;
    $[233] = t84;
  } else {
    t84 = $[233];
  }
  let t85;
  if ($[234] !== errors.email || $[235] !== t) {
    t85 = t86 => {
      const {
        field: field_10
      } = t86;
      return <section className="w-1/2"><Input id="email" {...field_10} label={t("forms.email_label")} type="email" errorMessage={typeof errors.email?.message === "string" ? errors.email.message : undefined} required={true} /></section>;
    };
    $[234] = errors.email;
    $[235] = t;
    $[236] = t85;
  } else {
    t85 = $[236];
  }
  let t86;
  if ($[237] !== Controller || $[238] !== control || $[239] !== t85) {
    t86 = <Controller name="email" control={control} render={t85} />;
    $[237] = Controller;
    $[238] = control;
    $[239] = t85;
    $[240] = t86;
  } else {
    t86 = $[240];
  }
  let t87;
  if ($[241] !== errors.phone || $[242] !== prefferedContactMethod || $[243] !== t) {
    t87 = t88 => {
      const {
        field: t89
      } = t88;
      const {
        value: value_5,
        onChange: onChange_5
      } = t89;
      return <section className="w-1/2"><Input id="phone" name="phone" value={value_5} onChange={onChange_5} label={t("forms.phone_label")} type="tel" errorMessage={typeof errors.phone?.message === "string" ? errors.phone.message : undefined} required={prefferedContactMethod} /></section>;
    };
    $[241] = errors.phone;
    $[242] = prefferedContactMethod;
    $[243] = t;
    $[244] = t87;
  } else {
    t87 = $[244];
  }
  let t88;
  if ($[245] !== Controller || $[246] !== control || $[247] !== t87) {
    t88 = <Controller name="phone" control={control} render={t87} />;
    $[245] = Controller;
    $[246] = control;
    $[247] = t87;
    $[248] = t88;
  } else {
    t88 = $[248];
  }
  let t89;
  if ($[249] !== t86 || $[250] !== t88) {
    t89 = <section className={styles.inlineFields}>{t86}{t88}</section>;
    $[249] = t86;
    $[250] = t88;
    $[251] = t89;
  } else {
    t89 = $[251];
  }
  let t90;
  if ($[252] !== errors.preferredContactMethod || $[253] !== t) {
    t90 = t91 => {
      const {
        field: field_11
      } = t91;
      return <RadioButtonGroup title={t("content.forms.contact_method_label")} id="preferredContactMethod" required={true} errorMessage={typeof errors.preferredContactMethod?.message === "string" ? errors.preferredContactMethod.message : undefined} {...field_11}><RadioButton id="Email" name="preferredContactMethod" value="email" checked={field_11.value === "email"} onChange={field_11.onChange}>{t("content.forms.contact_method_email")}</RadioButton><RadioButton id="Phone" name="preferredContactMethod" value="phone" checked={field_11.value === "phone"} onChange={field_11.onChange}>{t("content.forms.contact_method_phone")}</RadioButton></RadioButtonGroup>;
    };
    $[252] = errors.preferredContactMethod;
    $[253] = t;
    $[254] = t90;
  } else {
    t90 = $[254];
  }
  let t91;
  if ($[255] !== Controller || $[256] !== control || $[257] !== t90) {
    t91 = <Controller name="preferredContactMethod" control={control} render={t90} />;
    $[255] = Controller;
    $[256] = control;
    $[257] = t90;
    $[258] = t91;
  } else {
    t91 = $[258];
  }
  let t92;
  if ($[259] !== errors.leadPassengerCountry || $[260] !== t) {
    t92 = t93 => {
      const {
        field: field_12
      } = t93;
      return <Select id="leadPassengerCountry" {...field_12} label={t("content.forms.country_label")} selected={field_12.value} onChange={e_11 => field_12.onChange(e_11.target.value)} onBlur={field_12.onBlur} helpText={<span>{t("content.forms.country_help")}</span>} errorMessage={typeof errors.leadPassengerCountry?.message === "string" ? errors.leadPassengerCountry.message : undefined} required={true}><option value="" disabled={true}>{t("forms.country_placeholder")}</option>{leadPassengerCountryOptions.map(_temp13)}</Select>;
    };
    $[259] = errors.leadPassengerCountry;
    $[260] = t;
    $[261] = t92;
  } else {
    t92 = $[261];
  }
  let t93;
  if ($[262] !== Controller || $[263] !== control || $[264] !== t92) {
    t93 = <Controller name="leadPassengerCountry" control={control} render={t92} />;
    $[262] = Controller;
    $[263] = control;
    $[264] = t92;
    $[265] = t93;
  } else {
    t93 = $[265];
  }
  let t94;
  if ($[266] !== errors.howDidYouHearAboutUs || $[267] !== t) {
    t94 = t95 => {
      const {
        field: field_13
      } = t95;
      return <Select id="howDidYouHearAboutUs" {...field_13} label={t("content.forms.referral_label")} selected={field_13.value} onChange={e_12 => {
        const value_6 = e_12.target.value;
        field_13.onChange(value_6 === "" ? undefined : value_6);
      }} onBlur={field_13.onBlur} errorMessage={typeof errors.howDidYouHearAboutUs?.message === "string" ? errors.howDidYouHearAboutUs.message : undefined}><option value="" disabled={true}>{t("forms.select_placeholder")}</option><option value="Google">{t("content.forms.heard_google")}</option><option value="Other internet search">{t("content.forms.heard_internet_search")}</option><option value="Press">{t("content.forms.heard_press")}</option><option value="Social">{t("content.forms.heard_social")}</option><option value="Repeat customer">{t("content.forms.heard_repeat")}</option><option value="Referred by a friend or family member">{t("content.forms.heard_referral")}</option><option value="Seat 61">Seat 61</option><option value="Exodus">Exodus</option><option value="Intrepid">Intrepid</option><option value="Guardian Holidays">Guardian Holidays</option><option value="TravelAgent">{t("content.forms.heard_travel_agent")}</option><option value="Other">{t("content.forms.heard_other")}</option></Select>;
    };
    $[266] = errors.howDidYouHearAboutUs;
    $[267] = t;
    $[268] = t94;
  } else {
    t94 = $[268];
  }
  let t95;
  if ($[269] !== Controller || $[270] !== control || $[271] !== t94) {
    t95 = <Controller name="howDidYouHearAboutUs" control={control} render={t94} />;
    $[269] = Controller;
    $[270] = control;
    $[271] = t94;
    $[272] = t95;
  } else {
    t95 = $[272];
  }
  let t96;
  if ($[273] !== Controller || $[274] !== control || $[275] !== heardAbout || $[276] !== heardAboutDetailMeta || $[277] !== needsHeardAboutDetail || $[278] !== t) {
    t96 = needsHeardAboutDetail && <Controller name="howDidYouHearAboutUsDetail" control={control} rules={{
      required: t("content.feedback.detail_required")
    }} render={t97 => {
      const {
        field: t98
      } = t97;
      const {
        value: value_7,
        onChange: onChange_6
      } = t98;
      const meta = heardAboutDetailMeta[heardAbout];
      return <Input id={meta.id} name="heardAboutDetail" value={value_7 ?? ""} onChange={onChange_6} label={meta.label} placeholder={meta.placeholder} aria-label={meta.label} aria-placeholder={meta.placeholder} />;
    }} />;
    $[273] = Controller;
    $[274] = control;
    $[275] = heardAbout;
    $[276] = heardAboutDetailMeta;
    $[277] = needsHeardAboutDetail;
    $[278] = t;
    $[279] = t96;
  } else {
    t96 = $[279];
  }
  let t97;
  if ($[280] !== Controller || $[281] !== control || $[282] !== t) {
    t97 = !singleOptIn && <Controller name="newsletterSignup" control={control} render={t98 => {
      const {
        field: field_14
      } = t98;
      return <Checkbox id="newsletterSignup" name={field_14.name} checked={field_14.value} onChange={e_13 => field_14.onChange(e_13.target.checked)} label={t("content.forms.newsletter_signup")} aria-describedby="subscribeHelpText" />;
    }} />;
    $[280] = Controller;
    $[281] = control;
    $[282] = t;
    $[283] = t97;
  } else {
    t97 = $[283];
  }
  let t98;
  if ($[284] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = singleOptIn && <PrivacyNotice />;
    $[284] = t98;
  } else {
    t98 = $[284];
  }
  let t99;
  if ($[285] !== control) {
    t99 = <MarketingOptOutField control={control} />;
    $[285] = control;
    $[286] = t99;
  } else {
    t99 = $[286];
  }
  let t100;
  if ($[287] !== privacyApplies) {
    t100 = !singleOptIn && privacyApplies ? <PrivacyNotice /> : null;
    $[287] = privacyApplies;
    $[288] = t100;
  } else {
    t100 = $[288];
  }
  const t101 = submitStatus?.state;
  const t102 = submitStatus?.message;
  let t103;
  if ($[289] !== t101 || $[290] !== t102) {
    t103 = <div ref={outcomeRef} tabIndex={-1}><Notification state={t101} message={t102} /></div>;
    $[289] = t101;
    $[290] = t102;
    $[291] = t103;
  } else {
    t103 = $[291];
  }
  const t104 = singleOptIn || privacyApplies ? PRIVACY_NOTICE_ID : undefined;
  let t105;
  if ($[292] !== t) {
    t105 = t("forms.submit");
    $[292] = t;
    $[293] = t105;
  } else {
    t105 = $[293];
  }
  let t106;
  if ($[294] !== t105) {
    t106 = <span>{t105}</span>;
    $[294] = t105;
    $[295] = t106;
  } else {
    t106 = $[295];
  }
  let t107;
  if ($[296] !== isSubmitting || $[297] !== t104 || $[298] !== t106) {
    t107 = <Button data-ua-click="concierge_form_submission" type="submit" size="fullWidth" isLoading={isSubmitting} aria-describedby={t104}>{t106}</Button>;
    $[296] = isSubmitting;
    $[297] = t104;
    $[298] = t106;
    $[299] = t107;
  } else {
    t107 = $[299];
  }
  let t108;
  if ($[300] !== t100 || $[301] !== t103 || $[302] !== t107 || $[303] !== t42 || $[304] !== t43 || $[305] !== t44 || $[306] !== t46 || $[307] !== t48 || $[308] !== t50 || $[309] !== t52 || $[310] !== t54 || $[311] !== t56 || $[312] !== t58 || $[313] !== t60 || $[314] !== t62 || $[315] !== t64 || $[316] !== t69 || $[317] !== t70 || $[318] !== t72 || $[319] !== t74 || $[320] !== t75 || $[321] !== t77 || $[322] !== t79 || $[323] !== t84 || $[324] !== t89 || $[325] !== t91 || $[326] !== t93 || $[327] !== t95 || $[328] !== t96 || $[329] !== t97 || $[330] !== t99) {
    t108 = <section className={styles.wrapper}><section className={styles.inner} data-test-id="ConciergeForm"><form ref={formRef} onSubmit={t42} noValidate={true} className={styles.form}>{t43}{t44}{t46}{t48}{t50}{t52}{t54}{t56}{t58}{t60}{t62}{t64}{t69}{t70}{t72}{t74}{t75}{t77}{t79}{t84}{t89}{t91}{t93}{t95}{t96}{t97}{t98}{t99}{t100}{t103}{t107}</form></section></section>;
    $[300] = t100;
    $[301] = t103;
    $[302] = t107;
    $[303] = t42;
    $[304] = t43;
    $[305] = t44;
    $[306] = t46;
    $[307] = t48;
    $[308] = t50;
    $[309] = t52;
    $[310] = t54;
    $[311] = t56;
    $[312] = t58;
    $[313] = t60;
    $[314] = t62;
    $[315] = t64;
    $[316] = t69;
    $[317] = t70;
    $[318] = t72;
    $[319] = t74;
    $[320] = t75;
    $[321] = t77;
    $[322] = t79;
    $[323] = t84;
    $[324] = t89;
    $[325] = t91;
    $[326] = t93;
    $[327] = t95;
    $[328] = t96;
    $[329] = t97;
    $[330] = t99;
    $[331] = t108;
  } else {
    t108 = $[331];
  }
  return t108;
};
export { ConciergeForm };
function _temp(t0) {
  const {
    id,
    label
  } = t0;
  return <option key={id} value={id}>{label}</option>;
}
function _temp2(a, b) {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}
function _temp3(opt) {
  if (opt === "czech-republic") {}
  if (opt === "north-macedonia") {}
  return <option id={opt} key={opt} value={opt}>{opt.toLowerCase() === "uk" ? "UK" : capitalizeWords(opt)}</option>;
}
function _temp4(t0) {
  const {
    id: id_0,
    label: label_0
  } = t0;
  return <option key={id_0} value={id_0}>{label_0}</option>;
}
function _temp5(e_2) {
  const data = (e_2 as InputEvent).data;
  if (data && /\D/.test(data)) {
    e_2.preventDefault();
  }
}
function _temp6(e_3) {
  const text = e_3.clipboardData.getData("text");
  if (/\D/.test(text)) {
    e_3.preventDefault();
  }
}
function _temp7(e_4) {
  const data_0 = (e_4 as InputEvent).data;
  if (data_0 && /\D/.test(data_0)) {
    e_4.preventDefault();
  }
}
function _temp8(e_5) {
  const text_0 = e_5.clipboardData.getData("text");
  if (/\D/.test(text_0)) {
    e_5.preventDefault();
  }
}
function _temp9(e_6) {
  const data_1 = (e_6 as InputEvent).data;
  if (data_1 && /\D/.test(data_1)) {
    e_6.preventDefault();
  }
}
function _temp10(e_7) {
  const text_1 = e_7.clipboardData.getData("text");
  if (/\D/.test(text_1)) {
    e_7.preventDefault();
  }
}
function _temp11(e_9) {
  const data_2 = (e_9 as InputEvent).data;
  if (data_2 && /\D/.test(data_2)) {
    e_9.preventDefault();
  }
}
function _temp12(e_10) {
  const text_2 = e_10.clipboardData.getData("text");
  if (/\D/.test(text_2)) {
    e_10.preventDefault();
  }
}
function _temp13(option) {
  return <option key={option} value={option}>{option}</option>;
}